import { Alert, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { alpha, Box, Grid } from "@mui/system";
import { Camera, Cancel, Check as CheckIcon } from '@mui/icons-material';
import MoneyIcon from '@mui/icons-material/Money';
import { ApartmentDto } from "../../../../types/propertyInventory/Apartment";
import { usePropertyInventory } from "../../../../hooks/usePropertyInventory";
import { useCallback, useEffect, useState } from "react";
import { CreateRentPayment, PaymentMethod } from "../../../../types/propertyInventory/RentPayment";
import { useAuth } from "../../../../context/AuthContext";
import Swal from "sweetalert2";

interface Props {
  addPaymentDialogOpen: boolean;
  setAddPaymentDialogOpen: (open: boolean) => void;
  apartmentById: ApartmentDto | null;
  handleAddPayment: () => void;
}
export const CreateRentPaymentDialog: React.FC<Props> = ({ addPaymentDialogOpen, setAddPaymentDialogOpen, apartmentById, handleAddPayment }) => {
  const { createRentPayment, isCreating, errorMessage } = usePropertyInventory();
  const { userToken } = useAuth();
  const [rentPaymentData, setRentPaymentData] = useState<CreateRentPayment>({
    apartmentId: apartmentById ? apartmentById.id : '',
    tenantId: apartmentById?.currentTenant ? apartmentById.currentTenant?.id : '',
    paymentMethod: PaymentMethod.efectivo,
    paidAmount: apartmentById ? apartmentById.baseRentAmount : 0,
    receiptPhoto: undefined,
    paymentDate: new Date().toISOString(),
    processedBy: userToken ? userToken.user.id : 0,
  });
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<string | null>(null);

  const handleInitialData = useCallback(() => {
    if (apartmentById) {
      setRentPaymentData({
        apartmentId: apartmentById ? apartmentById.id : '',
        tenantId: apartmentById?.currentTenant ? apartmentById.currentTenant?.id : '',
        paymentMethod: PaymentMethod.efectivo,
        paidAmount: apartmentById ? apartmentById.baseRentAmount : 0,
        receiptPhoto: undefined,
        paymentDate: new Date().toISOString(),
        processedBy: userToken ? userToken.id : 0,
      });
    }
  }, [apartmentById, setRentPaymentData, userToken]);

  useEffect(() => {
    handleInitialData();
  }, [handleInitialData]);

  const handleError = () => {
    let message = '';
    if (rentPaymentData.paymentMethod === undefined || rentPaymentData.paidAmount <= 0) {
      message += 'Por favor, complete todos los campos obligatorios.\n';
    }
    if (apartmentById && rentPaymentData.paidAmount > apartmentById.baseRentAmount) {
      message += 'El monto pagado no puede exceder el monto de la renta.\n';
    }
    if (rentPaymentData.paymentDate === '') {
      message += 'La fecha de pago es obligatoria.\n';
    }
    if (message) {
      setIsError(true);
      setError(message);
      return;
    } else {
      setIsError(false);
      setError('');
      return;
    }
  }
  const handleCreatePayment = async () => {
    setIsError(false);
    handleError();
    if (isError) {
      return;
    }
    if (apartmentById) {
      await createRentPayment(rentPaymentData);
      if (errorMessage) {
        setIsError(true);
        setError(errorMessage || 'Error al crear el pago de renta.');
        return;
      } else {
        Swal.fire({
          icon: 'success',
          title: 'Pago Registrado',
          text: 'El pago de renta ha sido registrado exitosamente.',
          confirmButtonColor: '#4DB6AC',
        });
        setAddPaymentDialogOpen(false);
      }
    }
    handleAddPayment();
  };


  return (
    <Dialog open={addPaymentDialogOpen} onClose={() => setAddPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon sx={{ color: '#4DB6AC' }} />
          <Typography variant="h6" fontWeight="bold">
            Registrar Pago de Renta
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>

          {/* Monto Pagado */}
          <Grid size={12}>
            <TextField
              fullWidth
              label="Monto Pagado"
              value={rentPaymentData.paidAmount}
              onChange={(e) => setRentPaymentData({ ...rentPaymentData, paidAmount: parseFloat(e.target.value) })}
              type="number"
              disabled
              required
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>{apartmentById?.currency === 'USD' ? '$' : '₡'}</Typography>,
              }}
              helperText="Monto basado en la renta del apartamento"
            />
          </Grid>
          {/* Método de Pago */}
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>Método de Pago</InputLabel>
              <Select label="Método de Pago" value={rentPaymentData.paymentMethod} onChange={(e) => setRentPaymentData({ ...rentPaymentData, paymentMethod: e.target.value as PaymentMethod })}>
                <MenuItem value={PaymentMethod.efectivo}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    💵 Efectivo
                  </Box>
                </MenuItem>
                <MenuItem value={PaymentMethod.transferencia}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    🏦 Transferencia Bancaria
                  </Box>
                </MenuItem>
                <MenuItem value={PaymentMethod.tarjetaCredito}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    💳 Tarjeta de Crédito
                  </Box>
                </MenuItem>
                <MenuItem value={PaymentMethod.tarjetaDebito}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    💳 Tarjeta de Débito
                  </Box>
                </MenuItem>
                <MenuItem value={PaymentMethod.sinpe}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    📱 SINPE Móvil
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Fecha de Pago */}
          <Grid size={12}>
            <TextField
              fullWidth
              label="Fecha de Pago"
              type="date"
              defaultValue={new Date().toISOString().split('T')[0]}
              onChange={(e) => {
                const selectedDate = new Date(e.target.value);
                setRentPaymentData({ ...rentPaymentData, paymentDate: selectedDate.toISOString() });
              }}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          {/* Comprobante de Pago */}
          <Grid size={12}>
            <Box
              sx={{
                border: '2px dashed #4DB6AC',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  backgroundColor: alpha('#4DB6AC', 0.05),
                  borderColor: '#26A69A'
                }
              }}
            >
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="receipt-upload"
                type="file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setRentPaymentData({ ...rentPaymentData, receiptPhoto: file });
                    setPreview(URL.createObjectURL(file));
                  }
                }}
              />
              {!preview && (
              <label htmlFor="receipt-upload" style={{ cursor: 'pointer' }}>
                <Camera sx={{ fontSize: 40, color: '#4DB6AC', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Subir Comprobante de Pago
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  (Opcional - JPG, PNG)
                </Typography>
              </label>
              )}
              {/* Preview de imagen */}
              {preview && (
                <Box sx={{ mt: 2, position: 'relative', display: 'inline-block' }}>
                  <img
                    src={preview}
                    alt="Comprobante de pago"
                    style={{
                      width: '100%',
                      maxWidth: 200,
                      borderRadius: 8,
                      objectFit: 'cover',
                      border: '1px solid #ccc'
                    }}
                  />
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      setPreview(null);
                      setRentPaymentData({ ...rentPaymentData, receiptPhoto: undefined });
                    }}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      minWidth: 0,
                      padding: '2px 6px',
                      borderRadius: '50%'
                    }}
                  >
                    <Cancel fontSize="small" />
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Notas */}
          <Grid size={12}>
            <TextField
              fullWidth
              label="Notas"
              value={rentPaymentData.notes || ''}
              onChange={(e) => setRentPaymentData({ ...rentPaymentData, notes: e.target.value })}
              multiline
              rows={3}
              placeholder="Información adicional sobre el pago..."
              helperText="Comentarios o detalles relevantes (opcional)"
            />
          </Grid>

          {/* Resumen Visual */}
          <Grid size={12}>
            <Box
              sx={{
                backgroundColor: alpha('#4DB6AC', 0.1),
                borderRadius: 2,
                p: 2,
                mt: 1
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Resumen del Pago
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Inquilino:</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {apartmentById?.currentTenant?.fullName}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Apartamento:</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {apartmentById?.apartmentNumber}
                </Typography>
              </Box>
              <Chip
                icon={<MoneyIcon />}
                label={`Monto a Pagar: ${apartmentById?.currency === 'USD' ? '$' : '₡'}${apartmentById?.baseRentAmount.toFixed(2)}`}
                sx={{ backgroundColor: alpha('#4DB6AC', 0.2), fontWeight: 'bold' }}
              />
              <Divider sx={{ my: 1 }} />
            </Box>
          </Grid>
          {isError && (
            <Grid size={12}>
              <Alert severity="error" onClose={() => { setIsError(false); setError(''); }}>
                {error.split('\n').filter(Boolean).map((msg, index) => (
                  <div key={index}>{msg}</div>
                ))}
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={() => setAddPaymentDialogOpen(false)}>
          Cancelar
        </Button>
        <Button
          onClick={handleCreatePayment}
          variant="contained"
          startIcon={isCreating ? '' : <CheckIcon />}
          disabled={isCreating}
          sx={{
            backgroundColor: '#4DB6AC',
            '&:hover': {
              backgroundColor: '#26A69A'
            }
          }}
        >
          {isCreating ? <CircularProgress size={20} /> : 'Registrar Pago'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};